/**
 * Position Evaluator for Minimax AI
 * Scores board positions based on material, position, and tactical factors
 */

import { GameState, IPiece, PieceType, State } from "../../types";
import { isRaceVictory, RACE_VICTORY_BONUS } from "./MinimaxUtils";

/**
 * Piece values for material counting
 */
const PIECE_VALUES: Record<PieceType, number> = {
  [PieceType.Bear]: 300,
  [PieceType.Crane]: 350,
  [PieceType.Frog]: 350,
  [PieceType.Fungi]: 320,
  [PieceType.Goat]: 280,
  [PieceType.Monkey]: 340,
  [PieceType.Octopus]: 330,
  [PieceType.Penguin]: 310,
  [PieceType.Rhino]: 380,
  [PieceType.Snake]: 360,
  [PieceType.Tree]: 290,
  [PieceType.Wolf]: 370,
  [PieceType.Virus]: 300,
};

export class PositionEvaluator {
  /**
   * Evaluate a position from the perspective of the current player
   * Positive = good for current player, Negative = bad for current player
   */
  static evaluate(gameState: GameState, isMaximizing: boolean): number {
    // Check for terminal positions
    const terminalScore = this.evaluateTerminal(gameState, isMaximizing);
    if (terminalScore !== null) return terminalScore;

    // CRITICAL: Check for immediate race victories/defeats
    const raceScore = this.evaluateRaceThreats(gameState, isMaximizing);
    if (Math.abs(raceScore) > 50000) return raceScore; // Immediate win/loss

    let score = 0;

    // Material evaluation (most important)
    score += this.evaluateMaterial(gameState, isMaximizing) * 10;

    // King advancement (race condition)
    score += this.evaluateKingPosition(gameState, isMaximizing) * 15;

    // Piece positioning
    score += this.evaluatePiecePositioning(gameState, isMaximizing) * 2;

    // King safety (high weight — king survival is critical)
    score += this.evaluateKingSafety(gameState, isMaximizing) * 12;

    // Control of center
    score += this.evaluateCenterControl(gameState, isMaximizing) * 1;

    // Add race threats (non-immediate)
    score += raceScore;

    return score;
  }

  /**
   * Check for terminal game states (wins/losses)
   */
  private static evaluateTerminal(gameState: GameState, isMaximizing: boolean): number | null {
    if (gameState.state === State.PostGame) {
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return isMaximizing === gameState.isLightTurn ? winScore : -winScore;
      }
      if (gameState.lightWon === false) {
        return isMaximizing !== gameState.isLightTurn ? winScore : -winScore;
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }
    return null;
  }

  /**
   * Evaluate material balance
   */
  private static evaluateMaterial(gameState: GameState, isMaximizing: boolean): number {
    // BUG FIX: Calculate AI's color from isMaximizing, NOT from gameState.isLightTurn!
    // gameState.isLightTurn flips after each move, so we must infer AI's original color
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;
    let materialScore = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          // Pieces infected by a friendly virus are effectively dead
          // (virus will capture them when it leaves, per Rule 3.14f)
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              continue; // Don't count this piece — it's doomed
            }
          }

          const pieceValue = PIECE_VALUES[piece.type] || 300;
          const value = piece.isLight === perspective ? pieceValue : -pieceValue;

          // Kings are worth more
          const kingMultiplier = piece.isKing ? 2 : 1;

          // Penalize poisoned/entangled pieces
          // ENTANGLED: piece is completely immobilized! Worth almost nothing (0.1x value = -90% penalty)
          const statusPenalty = piece.poisonedStatus.isPoisoned ? 0.7 : piece.isEntangled ? 0.1 : 1;

          materialScore += value * kingMultiplier * statusPenalty;
        }
      }
    }

    return materialScore;
  }

  /**
   * CRITICAL: Evaluate immediate race victory threats
   * Returns massive scores for kings close to victory
   */
  private static evaluateRaceThreats(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;

    let ourKingRow = -1;
    let enemyKingRow = -1;

    // Find both kings
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing) continue;

          if (piece.isLight === perspective) {
            ourKingRow = row;
          } else {
            enemyKingRow = row;
          }
        }
      }
    }

    // Our king (light goes to row 7, dark goes to row 0)
    const ourGoalRow = perspective ? 7 : 0;
    const enemyGoalRow = perspective ? 0 : 7;

    let score = 0;

    // Check our king position
    if (ourKingRow !== -1) {
      const ourDistance = Math.abs(ourKingRow - ourGoalRow);

      if (ourDistance === 0) {
        // We already won! (shouldn't happen as game should be over)
        return 100000;
      } else if (ourDistance === 1) {
        // ONE MOVE FROM VICTORY!
        score += 80000;
      } else if (ourDistance === 2) {
        // Two moves from victory
        score += 40000;
      } else if (ourDistance === 3) {
        // Three moves from victory
        score += 20000;
      }
    }

    // Check enemy king position
    if (enemyKingRow !== -1) {
      const enemyDistance = Math.abs(enemyKingRow - enemyGoalRow);

      if (enemyDistance === 0) {
        // Enemy won! (shouldn't happen)
        return -100000;
      } else if (enemyDistance === 1) {
        // ENEMY ONE MOVE FROM VICTORY! PANIC!
        score -= 90000; // Even worse than our win being close!
      } else if (enemyDistance === 2) {
        // Enemy two moves from victory - danger!
        score -= 45000;
      } else if (enemyDistance === 3) {
        // Enemy three moves from victory - concerning
        score -= 22000;
      }
    }

    return score;
  }

  /**
   * Evaluate king positions (race advancement)
   */
  private static evaluateKingPosition(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing) continue;

          // Calculate distance to goal
          const goalRow = piece.isLight ? 7 : 0;
          const distanceToGoal = Math.abs(row - goalRow);
          const advancementScore = 8 - distanceToGoal; // Higher score for closer to goal

          if (piece.isLight === perspective) {
            score += advancementScore * 20;
          } else {
            score -= advancementScore * 20;
          }
        }
      }
    }

    return score;
  }

  /**
   * Evaluate general piece positioning
   */
  private static evaluatePiecePositioning(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing) continue; // Already evaluated above

          // Advancement bonus (pieces closer to enemy territory)
          const advancement = piece.isLight ? row : 7 - row;
          const advancementBonus = advancement * 2;

          if (piece.isLight === perspective) {
            score += advancementBonus;
          } else {
            score -= advancementBonus;
          }
        }
      }
    }

    return score;
  }

  /**
   * Evaluate king safety — checks for nearby enemies, friendly support,
   * and whether the king is dangerously exposed (advancing without escort).
   */
  private static evaluateKingSafety(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;
    let score = 0;

    // Find kings and all pieces for threat analysis
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing) continue;

          let friendlySupport = 0;
          let adjacentEnemies = 0;
          let nearbyEnemies = 0;

          // Check radius 1 (adjacent) — immediate threats and support
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;
              const r = row + dr;
              const c = col + dc;
              if (r < 0 || r >= 8 || c < 0 || c >= 5) continue;

              for (const nearby of gameState.board[r][c].Pieces) {
                if (nearby.isLight === piece.isLight) {
                  friendlySupport++;
                } else {
                  adjacentEnemies++;
                }
              }
            }
          }

          // Check radius 2 — pieces that can reach the king in one move
          for (let dr = -2; dr <= 2; dr++) {
            for (let dc = -2; dc <= 2; dc++) {
              if (Math.abs(dr) <= 1 && Math.abs(dc) <= 1) continue; // Already counted
              const r = row + dr;
              const c = col + dc;
              if (r < 0 || r >= 8 || c < 0 || c >= 5) continue;

              for (const nearby of gameState.board[r][c].Pieces) {
                if (nearby.isLight !== piece.isLight) {
                  nearbyEnemies++;
                }
              }
            }
          }

          // Adjacent enemies are extremely dangerous — they can likely capture the king
          let safetyScore = -adjacentEnemies * 500;

          // Nearby enemies (radius 2) are dangerous — many pieces move 1-2 squares
          safetyScore -= nearbyEnemies * 100;

          // Friendly support provides safety
          safetyScore += friendlySupport * 30;

          // CRITICAL: Exposed king penalty — king with NO friendly support is in grave danger
          if (friendlySupport === 0) {
            safetyScore -= 300;

            // Even worse if there are enemies within range
            if (adjacentEnemies > 0 || nearbyEnemies > 0) {
              safetyScore -= 500;
            }
          }

          // CRITICAL: King advancing ahead of all friendly pieces is reckless
          const goalRow = piece.isLight ? 7 : 0;
          const kingAdvancement = piece.isLight ? row : (7 - row);
          let friendsAtOrAhead = 0;
          for (let r2 = 0; r2 < 8; r2++) {
            for (let c2 = 0; c2 < 5; c2++) {
              for (const p of gameState.board[r2][c2].Pieces) {
                if (p.isLight === piece.isLight && !p.isKing) {
                  const pAdvancement = piece.isLight ? r2 : (7 - r2);
                  if (pAdvancement >= kingAdvancement - 1) {
                    friendsAtOrAhead++;
                  }
                }
              }
            }
          }

          // King rushing ahead alone — massive penalty
          if (friendsAtOrAhead === 0 && kingAdvancement >= 2) {
            safetyScore -= 800;
          }

          if (piece.isLight === perspective) {
            score += safetyScore;
          } else {
            score -= safetyScore;
          }
        }
      }
    }

    return score;
  }

  /**
   * Evaluate control of center columns
   */
  private static evaluateCenterControl(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;
    let score = 0;

    // Center columns are 1, 2, 3
    for (let row = 0; row < 8; row++) {
      for (let col = 1; col <= 3; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const centerValue = col === 2 ? 3 : 2; // Middle column worth more

          if (piece.isLight === perspective) {
            score += centerValue;
          } else {
            score -= centerValue;
          }
        }
      }
    }

    return score;
  }

  /**
   * Quick material-only evaluation for move ordering
   */
  static quickEvaluate(gameState: GameState, isMaximizing: boolean): number {
    return this.evaluateMaterial(gameState, isMaximizing);
  }
}